import { Box, Typography } from "@material-ui/core";
import React, { useMemo } from "react";
import { PackageItinerary, RedeemedEarnOffersResponse } from "redmond";
import {
  getTotalPriceText,
  Icon,
  IconName,
  twoDecimalFormatter,
  EarnOfferBanner,
} from "halifax";
import * as textConstants from "./constants";
import "./styles.scss";
import { getCurrencyString } from "../../../../../../../../utils/helpers";
import { formatPaymentBreakdown, ILineItem } from "../../../FlightCard/helpers";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DueAtPropertyAmount } from "@b2bportal/lodging-api";
import {
  ActiveExperiments,
  CONTROL,
  getExperimentVariant,
  useExperiments,
} from "../../../../../../../../context/experiments";

interface IPackagePaymentModalContent {
  packageItinerary: PackageItinerary;
  redeemedEarnOffers: RedeemedEarnOffersResponse["redeemedEarnOffers"];
}
export const PackagePaymentModalContent = (
  props: IPackagePaymentModalContent
) => {
  const { packageItinerary, redeemedEarnOffers } = props;
  const { pricingBreakdown, paymentBreakdown, hotel } = packageItinerary;
  const { subtotalWithoutTaxesAndFees, taxesAndFeesTotal } = pricingBreakdown;
  const { taxesAndFeesDueAtProperty } =
    hotel.itinerary.reservation.pricingV2 ?? {};

  const expState = useExperiments();

  const eanCertExperiment = getExperimentVariant(
    expState.experiments,
    ActiveExperiments.EAN_CERTIFICATION_EXPERIMENT
  );
  const isEANCertExperiment = useMemo(
    () => eanCertExperiment !== CONTROL,
    [eanCertExperiment]
  );

  const renderDueLaterLine = (
    amountType: string,
    lineItem?: DueAtPropertyAmount
  ) => {
    if (lineItem?.value.fiat && lineItem?.value.fiat.value > 0) {
      return (
        <Box className="package-pricing-row">
          <Typography variant="body2">{amountType}</Typography>
          {lineItem?.valueInOriginalCurrency.fiat.currencyCode !==
          lineItem?.value.fiat.currencyCode ? (
            <Typography variant="body2">
              {getTotalPriceText({
                price: lineItem?.valueInOriginalCurrency.fiat,
                priceFormatter: twoDecimalFormatter,
              })}

              {` (${getTotalPriceText({
                price: lineItem?.value.fiat,
                priceFormatter: twoDecimalFormatter,
              })}) (due at hotel)`}
            </Typography>
          ) : (
            <Typography variant="body2">
              {`${getTotalPriceText({
                price: lineItem?.value.fiat,
                priceFormatter: twoDecimalFormatter,
              })} (due at hotel)`}
            </Typography>
          )}
        </Box>
      );
    }
    return undefined;
  };
  const renderPricingBreakdown = () => (
    <Box className="package-pricing-breakdown">
      {isEANCertExperiment ? (
        <>
          {subtotalWithoutTaxesAndFees?.totalAmount.fiat &&
            (subtotalWithoutTaxesAndFees?.totalAmount.fiat.value ?? 0) > 0 && (
              <Box className="package-pricing-row">
                <Typography variant="body2">
                  {`Subtotal (${
                    pricingBreakdown?.subtotal.numTravelers
                  } traveler${
                    pricingBreakdown?.subtotal.numTravelers > 1 ? "s" : ""
                  })`}
                </Typography>
                <Typography variant="body2">
                  {getTotalPriceText({
                    price: subtotalWithoutTaxesAndFees?.totalAmount.fiat,
                    priceFormatter: twoDecimalFormatter,
                  })}
                </Typography>
              </Box>
            )}
          {taxesAndFeesTotal?.fiat &&
            (taxesAndFeesTotal?.fiat.value ?? 0) > 0 && (
              <Box className="package-pricing-row">
                <Typography variant="body2">Taxes and fees</Typography>
                <Typography variant="body2">
                  {getTotalPriceText({
                    price: taxesAndFeesTotal?.fiat,
                    priceFormatter: twoDecimalFormatter,
                  })}
                </Typography>
              </Box>
            )}
          {(taxesAndFeesDueAtProperty?.total?.value.fiat.value ?? 0) > 0 && (
            <Box>
              {renderDueLaterLine(
                textConstants.MANDATORY_TAXES,
                taxesAndFeesDueAtProperty.mandatoryTaxes
              )}
              {renderDueLaterLine(
                textConstants.MANDATORY_FEES,
                taxesAndFeesDueAtProperty.mandatoryFees
              )}

              {renderDueLaterLine(
                textConstants.RESORT_FEES,
                taxesAndFeesDueAtProperty.resortFees
              )}
            </Box>
          )}
        </>
      ) : (
        <>
          <Box className="package-pricing-row">
            <Typography variant="body2">
              Starting price per traveler (
              {pricingBreakdown?.subtotal.numTravelers} travelers)
            </Typography>
            <Typography variant="body2">
              {getTotalPriceText({
                price: pricingBreakdown.subtotal.perTravelerAmount.fiat,
                priceFormatter: twoDecimalFormatter,
              })}
            </Typography>
          </Box>
          <Box className="package-pricing-row">
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">
              {getTotalPriceText({
                price: pricingBreakdown.subtotal.totalAmount.fiat,
                priceFormatter: twoDecimalFormatter,
              })}
            </Typography>
          </Box>
        </>
      )}

      {pricingBreakdown.savingsAmount?.fiat &&
        pricingBreakdown.savingsAmount?.fiat.value < 0 && (
          <Box className="package-pricing-row savings">
            <Typography variant="body2">
              {textConstants.PACKAGE_SAVINGS}
            </Typography>
            <Typography variant="body2">
              {getTotalPriceText({
                price: pricingBreakdown.savingsAmount?.fiat,
                priceFormatter: twoDecimalFormatter,
              })}
            </Typography>
          </Box>
        )}
      {pricingBreakdown.seatsAmount && (
        <Box className="package-pricing-row seats">
          <Typography variant="body2">
            {textConstants.SEAT_SELECTION}
          </Typography>
          <Typography variant="body2">
            {getTotalPriceText({
              price: pricingBreakdown.seatsAmount?.fiat,
              priceFormatter: twoDecimalFormatter,
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTripTotal = () => (
    <Box className="trip-totals">
      <Box className="package-pricing-row">
        <Typography variant="body1" className="trip-total-text price-bold-row">
          {textConstants.TOTAL_DUE_TODAY}
        </Typography>
        <Typography variant="body1" className="trip-total-text price-bold-row">
          {getCurrencyString(
            packageItinerary.pricingBreakdown.payNowAmount.fiat
          )}
        </Typography>
      </Box>
      {isEANCertExperiment
        ? renderDueLaterLine(
            textConstants.DUE_AT_CHECKIN,
            taxesAndFeesDueAtProperty?.total
          )
        : pricingBreakdown.payAtHotelAmount && (
            <Box className="package-pricing-row savings">
              <Typography variant="body2">
                {textConstants.HOTEL_SERVICE_FEE}
              </Typography>
              <Typography variant="body2">
                {getTotalPriceText({
                  price: pricingBreakdown.payAtHotelAmount?.fiat,
                  priceFormatter: twoDecimalFormatter,
                })}{" "}
                (due at hotel)
              </Typography>
            </Box>
          )}
    </Box>
  );

  const lineItems =
    paymentBreakdown && formatPaymentBreakdown(paymentBreakdown);

  const renderRewards = () => {
    const rewards = lineItems?.rewards;
    if (!rewards) {
      return null;
    }
    return (
      <Box className="rewards-container">
        <Typography variant="body2">{rewards.label}</Typography>
        <Typography variant="body1" className="rewards-points">
          {rewards.amount}
        </Typography>
      </Box>
    );
  };

  const renderPayment = () => {
    const userCard = lineItems?.userCard;
    if (!userCard) {
      return null;
    }
    return (
      <Box className="payment-card-container">
        <Box className="payment-card-details-container">
          <FontAwesomeIcon icon={faCreditCard} className="credit-card-icon" />
          <Typography variant="body2" className="payment-card-details">
            {userCard.label}
          </Typography>
        </Box>
        <Typography variant="body1" className="payment-card-amount">
          {userCard.amount}
        </Typography>
      </Box>
    );
  };

  const renderTravelWallet = (
    travelWallet: ILineItem | null,
    icon: IconName
  ) => {
    if (!travelWallet) return null;
    return (
      <Box className="offers-container">
        <Box className="offers-icon-label">
          <Icon name={icon} />
          <Typography variant="body2">{travelWallet.label}</Typography>
        </Box>
        <Typography variant="body1" className="offer-amount">
          {travelWallet.amount}
        </Typography>
      </Box>
    );
  };

  const renderOffers = () =>
    lineItems &&
    renderTravelWallet(lineItems?.travelWalletOffer, IconName.OfferTag);

  const renderCredits = () =>
    lineItems?.travelWalletCredit?.map((credit) =>
      renderTravelWallet(credit, IconName.PiggyBank)
    );

  const renderEarnOffer = () => (
    <Box className="earn-offer-container">
      {redeemedEarnOffers.length > 0 ? <EarnOfferBanner /> : null}
    </Box>
  );

  return (
    <Box className="package-payment-modal-content">
      <Box className="package-payment-title-container">
        <Typography variant="h4" className="title-text">
          {textConstants.CHECKOUT_BREAKDOWN}
        </Typography>
      </Box>
      {renderPricingBreakdown()}
      {renderTripTotal()}
      {renderOffers()}
      {renderCredits()}
      {renderRewards()}
      {renderPayment()}
      {renderEarnOffer()}
    </Box>
  );
};
